import React, { useState } from "react";
import {
  IconX,
  IconSearch,
  IconUserCheck,
  IconUserPlus,
  IconLoader2,
} from "@tabler/icons-react";
import { useFetchStaffsAllData } from "../../../apis/staffApis";
import { useAssignPicklistMutation } from "../../../apis/picklistApis";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import NormalSearch from "../../../components/common/NormalSearch";
import { useLanguage } from "../../../context/LanguageContext";

interface StaffMember {
  id: string;
  name: string;
  email: string;
  code?: string;
  status?: string;
  photo?: string;
}

interface AssignStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  picklistId: string | null;
  currentAssignedId?: string | null;
}

const AssignStaffModal: React.FC<AssignStaffModalProps> = ({
  isOpen,
  onClose,
  picklistId,
  currentAssignedId,
}) => {
  const { data: staffData, isLoading } = useFetchStaffsAllData();
  const assignMutation = useAssignPicklistMutation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const { t } = useLanguage();

  const handleAssign = (staffId: string) => {
    if (!picklistId) return;
    if (staffId === currentAssignedId) {
      toast.error("Staff is already assigned to this picklist");
      return;
    }

    assignMutation.mutate(
      { id: picklistId, assignedTo: staffId },
      {
        onSuccess: () => {
          toast.success("Staff assigned successfully");
          queryClient.invalidateQueries({ queryKey: ["picklists"] });
          queryClient.invalidateQueries({ queryKey: ["picklist", picklistId] });
          onClose();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to assign staff");
        },
      },
    );
  };

  if (!isOpen) return null;

  const filteredStaff = Array.isArray(staffData)
    ? staffData.filter(
        (staff: StaffMember) =>
          staff?.name?.toLowerCase().includes(search.toLowerCase()) ||
          staff?.email?.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  return (
    <>
      {/* <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
        <div className="w-full max-w-[500px] relative animate-in fade-in zoom-in duration-300"> 
          <div className="flex items-center">
            <div className="bg-[#FDF8E5] flex items-center justify-between px-8 py-5 w-full rounded-tl-[40px] rounded-tr-[80px] shadow-sm">
              <h3 className="text-xl font-bold text-[#212121]">Assign Staff</h3>
            </div>
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={38}
                height={37}
                viewBox="0 0 38 37"
                fill="none"
                className="absolute -bottom-[1px] -left-[30px] rotate-[-2deg]"
              >
                <path
                  d="M0.5 0L4.86189 10.5496C10.4737 24.1222 22.9306 33.645 37.5 35.5L4.91509 37L0.5 0Z"
                  fill="#FCFDF8"
                />
              </svg>
              <button
                onClick={onClose}
                className="bg-[#F2F2F7] hover:bg-gray-200 rounded-full p-1 cursor-pointer w-12 h-12 ml-4 flex items-center justify-center flex-shrink-0 shadow-sm border border-white/50 transition-all"
              >
                <IconX size={20} className="text-[#212121]" />
              </button>
            </div>
          </div> 
          <div className="bg-[#FCFDF8] rounded-b-[40px] rounded-tr-[40px] -mt-1 h-[65vh] flex flex-col shadow-2xl border-t border-white/20 overflow-hidden">
 
            <div className="px-8 pt-8 pb-4">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search staff name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-white border border-gray-100 rounded-2xl px-6 py-4 text-sm font-bold text-gray-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FBD037]/20 transition-all pr-14"
                />
                <div className="absolute right-5 top-1/2 -translate-y-1/2 p-2 bg-[#FBD037]/10 rounded-xl text-[#FBD037]">
                  <IconSearch size={20} stroke={2.5} />
                </div>
              </div>
            </div> 
            <div className="flex-1 overflow-y-auto px-8 py-4 space-y-4 hide-scrollbar">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
                  <IconLoader2
                    size={48}
                    className="animate-spin text-[#FBD037] opacity-50"
                  />
                  <span className="font-bold text-sm tracking-widest uppercase">
                    Loading Staff
                  </span>
                </div>
              ) : filteredStaff.length > 0 ? (
                filteredStaff.map((staff: StaffMember) => {
                  const isAssigned = staff.id === currentAssignedId;
                  return (
                    <div
                      key={staff.id}
                      className={`flex items-center justify-between group p-3 rounded-2xl border transition-all hover:shadow-md ${isAssigned ? "bg-[#FBD037]/5 border-[#FBD037]/20" : "bg-white border-gray-50 hover:border-[#FBD037]/30"}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <img
                            src={
                              staff.photo ||
                              "https://i.pravatar.cc/150?u=" + staff.id
                            }
                            alt={staff.name}
                            className="w-14 h-14 rounded-2xl object-cover border-2 border-white shadow-sm transition-transform group-hover:scale-105"
                          />
                          {isAssigned && (
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#20BF55] rounded-full border-[3px] border-white shadow-sm flex items-center justify-center">
                              <IconUserCheck
                                size={10}
                                className="text-white"
                                stroke={3}
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-black text-[#212121] group-hover:text-[#FBD037] transition-colors">
                            {staff.name}
                          </span>
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                            {staff.email}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleAssign(staff.id)}
                        disabled={assignMutation.isPending || isAssigned}
                        className={`p-3.5 rounded-xl transition-all shadow-sm flex items-center justify-center ${
                          isAssigned
                            ? "bg-green-50 text-green-500 cursor-default"
                            : "bg-[#F7F6F2] text-[#212121] hover:bg-[#FBD037] hover:shadow-lg hover:shadow-[#FBD037]/20"
                        }`}
                      >
                        {assignMutation.isPending &&
                        assignMutation.variables?.assignedTo === staff.id ? (
                          <IconLoader2 size={20} className="animate-spin" />
                        ) : isAssigned ? (
                          <IconUserCheck size={20} stroke={2.5} />
                        ) : (
                          <IconUserPlus size={20} stroke={2.5} />
                        )}
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                  <IconSearch size={40} className="opacity-20" />
                  <span className="font-bold">
                    No staff found matching "{search}"
                  </span>
                </div>
              )}
            </div> 
            <div className="h-8 bg-gradient-to-t from-[#FCFDF8] to-transparent pointer-events-none sticky bottom-0"></div>
          </div>
        </div>
      </div> */}

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-[#F7F6F2] rounded-3xl w-[500px] h-[63vh] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-lg font-medium text-[#212121]">{t("Assign Staff")}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full border border-[#D2D2D2]"
            >
              <IconX size={20} />
            </button>
          </div>

          {/* Search */}
          <div className="px-4 py-2">
            <div className="relative group">
              <input
                type="text"
                placeholder={t("Search staff name...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-white border border-gray-100 rounded-2xl px-6 py-4 text-sm font-bold text-gray-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#FBD037]/20 transition-all pr-14"
              />
              <div className="absolute right-5 top-1/2 -translate-y-1/2 p-2 bg-[#FBD037]/10 rounded-xl text-[#FBD037]">
                <IconSearch size={20} stroke={2.5} />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 hide-scrollbar px-4 py-2">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
                <IconLoader2
                  size={48}
                  className="animate-spin text-[#FBD037] opacity-50"
                />
                <span className="font-bold text-sm tracking-widest uppercase">
                  {t("Loading Staff")}
                </span>
              </div>
            ) : filteredStaff.length > 0 ? (
              filteredStaff.map((staff: StaffMember) => {
                const isAssigned = staff.id === currentAssignedId;
                return (
                  <div
                    key={staff.id}
                    className={`flex items-center justify-between group p-3 rounded-2xl border transition-all hover:shadow-md ${isAssigned ? "bg-[#FBD037]/5 border-[#FBD037]/20" : "bg-white border-gray-50 hover:border-[#FBD037]/30"}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <img
                          src={
                            staff.photo ||
                            "https://i.pravatar.cc/150?u=" + staff.id
                          }
                          alt={staff.name}
                          className="w-14 h-14 rounded-2xl object-cover border-2 border-white shadow-sm transition-transform group-hover:scale-105"
                        />
                        {isAssigned && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#20BF55] rounded-full border-[3px] border-white shadow-sm flex items-center justify-center">
                            <IconUserCheck
                              size={10}
                              className="text-white"
                              stroke={3}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-black text-[#212121] group-hover:text-[#FBD037] transition-colors">
                          {staff.name}
                        </span>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                          {staff.email}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleAssign(staff.id)}
                      disabled={assignMutation.isPending || isAssigned}
                      className={`p-3.5 rounded-xl transition-all shadow-sm flex items-center justify-center ${
                        isAssigned
                          ? "bg-green-50 text-green-500 cursor-default"
                          : "bg-[#F7F6F2] text-[#212121] hover:bg-[#FBD037] hover:shadow-lg hover:shadow-[#FBD037]/20"
                      }`}
                    >
                      {assignMutation.isPending &&
                      assignMutation.variables?.assignedTo === staff.id ? (
                        <IconLoader2 size={20} className="animate-spin" />
                      ) : isAssigned ? (
                        <IconUserCheck size={20} stroke={2.5} />
                      ) : (
                        <IconUserPlus size={20} stroke={2.5} />
                      )}
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                <IconSearch size={40} className="opacity-20" />
                <span className="font-bold">
                  {t("No staff found matching")} "{search}"
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignStaffModal;
